import StyledImage from '@apps/components/Image';
import { ProgressBar } from '@apps/components/ProgressBar';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import { Stack } from '@mui/material';
import AcIcon from '@stack/assets/icons/ac-icon.svg';
import DcIcon from '@stack/assets/icons/dc-icon.svg';
import createBasePath from '@stack/helpers/lib/createBasePath';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import styled from 'styled-components';

import { EvTestCarCardProps } from './props';

type RenderProgressBarProps = {
  percentage: string | number;
  value: string | number;
  suffix: string;
  prefixIcon?: React.ReactNode;
  isSecondary?: boolean;
};

const ImageWrapper = styled.div<{
  minWidth?: string;
  width: string;
}>`
  width: ${(props) => props.width};
  min-width: ${(props) => props.minWidth ?? props.width};
  height: 100%;
  margin: auto;
  transition: transform 0.3s;
  &:hover {
    transform: scale(1.1);
  }
`;

export const EvTestCarCard: React.FC<EvTestCarCardProps> = (props) => {
  const { cardType, data, webView } = props;

  const { isMobile, sm } = useGetResponsive();
  const { clickEvent } = usePostMsgContext();
  const { basePath } = createBasePath({ webView, type: 'cars' });

  const newCarHref = `${basePath}/${data.car_brand_slug}/${data.new_car_slug}`;
  const widthPercentage = `${isMobile ? 100 : 85}%`;
  const suffix = (() => {
    switch (cardType) {
      case 'top_range_performance':
        return 'km';
      case 'top_fast_charging_times':
        return 'mins';
      case 'top_power_performance':
        return 's';
    }
  })();

  const onClick = clickEvent(newCarHref, false, {
    title: data.new_car_title,
    params: {
      brand: data.car_brand_slug,
      slug: data.new_car_slug,
    },
  });

  const renderProgressBar = (props: RenderProgressBarProps) => {
    const { percentage, value, suffix, prefixIcon, isSecondary } = props;
    return (
      <ProgressBar
        percentage={percentage}
        foregroundProps={{
          minWidth: 'min-content',
          ...(isSecondary
            ? {
                backgroundColor: '#FFC800',
              }
            : undefined),
        }}
      >
        <Stack
          flexDirection={'row'}
          justifyContent={'flex-end'}
          alignItems={'center'}
          marginRight={2}
          flexWrap={'nowrap'}
        >
          {prefixIcon}
          <p
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: isSecondary ? '#1E242F' : '#fff',
              whiteSpace: 'nowrap',
            }}
          >{`${value} ${suffix}`}</p>
        </Stack>
      </ProgressBar>
    );
  };

  return (
    <a
      href={newCarHref}
      onClick={onClick}
      style={{
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <Stack
        flexDirection={isMobile ? 'row' : 'column'}
        justifyContent={isMobile ? 'flex-start' : 'center'}
        gap={isMobile ? 5 : 1}
        width={widthPercentage}
        margin={'auto'}
      >
        <ImageWrapper
          width={isMobile ? '90px' : '100%'}
          minWidth={sm ? undefined : '120px'}
        >
          <StyledImage
            src={data.car_model_image}
            alt={data.car_brand_slug}
            width={175}
            height={92}
          />
        </ImageWrapper>

        <Stack
          flexGrow={1}
          overflow={'hidden'}
          gap={isMobile ? 0 : 1}
          alignSelf={isMobile ? 'center' : undefined}
        >
          <p
            style={{
              fontSize: 16,
              fontWeight: 700,
              textAlign: isMobile ? 'start' : 'center',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              color: '#1E242F',
              textOverflow: 'ellipsis',
            }}
          >
            {data.new_car_title}
          </p>

          <Stack gap={1}>
            {renderProgressBar({
              percentage: data.car_variant_spec_percent,
              value: data.car_variant_spec_value,
              suffix,
              prefixIcon: cardType === 'top_fast_charging_times' && <DcIcon />,
            })}

            {cardType === 'top_fast_charging_times' &&
              data.secondary_car_variant_spec_percent &&
              data.secondary_car_variant_spec_value &&
              renderProgressBar({
                percentage: data.secondary_car_variant_spec_percent,
                value: data.secondary_car_variant_spec_value,
                suffix: 'h',
                prefixIcon: <AcIcon />,
                isSecondary: true,
              })}
          </Stack>
        </Stack>
      </Stack>
    </a>
  );
};
